import React, { ReactElement, ReactNode } from 'react'

import { useAppSelector } from '../../../store/hooks'
import { BaseContract, CtgContract } from '../../../store/slices/contractSlice'
import TariffConditionDownloads from '../TariffConditionDownloads'

interface TariffConditionProps {
  children?: ReactNode;
  productType: string
  productReduceCharges?: boolean
}

interface Section {
  [key: string]: string | number | Section;
}

interface ConditionInformation {
  Section1: Section;
  Section2: Section;
  Section3: Section;
  Section4: Section;
}

const Component = ({ children, productType, productReduceCharges }: TariffConditionProps): ReactElement => {
  const customerInputValues = useAppSelector(state => state.customervalues)
  const heatPumpConsumption = customerInputValues['p-intro']['intro-wae-heat-pump']
  const contractState = useAppSelector(state => state.contract)
  const baseContractSlice = contractState.baseContract as BaseContract
  const date = new Date()
  const options = { day: '2-digit', month: '2-digit', year: 'numeric' }
  const today = (date as any).toLocaleDateString('de-DE', options)
  const data : ConditionInformation = {
    Section1: {},
    Section2: {},
    Section3: {},
    Section4: {}
  }

  let smallPrint = 'Bruttopreise inkl. 19% Umsatzsteuer und aller sonstiger Preisbestandteile. ' +
    'Die Erlöse aus der Einspeisung bewerten wir als brutto. ' +
    'Die hier dargestellten Angebote richten sich an Neukunden. Neukunde ist, wer in den letzten sechs Monaten ' +
    'vor Auftragseingang am angegebenen Zähler (Zählpunkt) kein SENEC Kunde war. ' +
    'Weitere Informationen können Sie in den Dokumenten zum Tarif nachlesen.'

  let slice
  let yearlyConsumption = ''
  let EnergyCostsFormatting = 0
  let totalDifferenceOfEnergyCosts = ''
  let totalCostsNotCappedByGrid = 0
  switch (productType) {
    case 'Cloud':
      slice = baseContractSlice
      // Convert negative value to positive and then localize.
      yearlyConsumption = slice.calculatedGridImportInKwhPerYear
      totalCostsNotCappedByGrid = Number(slice.totalCostsNotCappedByGridExportPayment)
      EnergyCostsFormatting = Number(slice.totalDifferenceOfEnergyCostsAndRenumerationInEuroWithoutVat)
      EnergyCostsFormatting = Math.abs(EnergyCostsFormatting)
      if (Number.isInteger(EnergyCostsFormatting)) {
        totalDifferenceOfEnergyCosts = EnergyCostsFormatting.toLocaleString('de-DE')
      } else {
        totalDifferenceOfEnergyCosts = EnergyCostsFormatting.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }
      if (totalCostsNotCappedByGrid > 0) {
        totalDifferenceOfEnergyCosts = '0'
      }
      break
    case 'Wärme':
      slice = contractState.heatContract as BaseContract
      yearlyConsumption = String(slice.totalConsumptionInKwhPerYear)
      break
    case 'Cloud to go':
      slice = contractState.ctgContract as CtgContract
      break
    case 'Family & Friends 1':
      slice = contractState.faf1Contract as BaseContract
      yearlyConsumption = String(slice.totalConsumptionInKwhPerYear)
      break
    case 'Family & Friends 2':
      slice = contractState.faf2Contract as BaseContract
      yearlyConsumption = String(slice.totalConsumptionInKwhPerYear)
      break
    default:
      throw new Error('ProductType Selection Error' + productType)
  }

  if (!slice) {
    throw new Error('Slice is undefined')
  }

  function formatNumber (value: string, defaultValue = '', convertToMinus = false): string {
    let number = parseFloat(value)
    if (isNaN(number)) {
      return defaultValue
    } else {
      if (convertToMinus) {
        number = (number <= 0) ? number : -Math.abs(number)
      }
      if (Number.isInteger(number)) {
        return number.toLocaleString('de-DE')
      } else {
        return number.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }
    }
  }

  function getCloudData (slice: BaseContract) : ConditionInformation {
    data.Section1 = {
      'Erwarteter Jahresverbrauch*': formatNumber(yearlyConsumption) + ' kWh',
      'Grundpreis pro Monat': formatNumber(slice.basePriceWithVatInEuroPerMonth) + ' €',
      'Verbrauchspreis pro kWh': formatNumber(slice.workingPriceWithVatInCtPerKWh) + ' Cent',
      smallPrint: '*Der erwartete Jahresverbrauch ist der für Sie prognostizierte Reststrombedarf aus Netzbezug ' +
        'nach Abzug Ihres Eigenverbrauchs aus PV-Anlage und Speicher.'
    }
    if (productReduceCharges) {
      data.Section2 = {
        'Erwarteter Gesamtpreis für die Stromlieferung': formatNumber(slice.totalPriceWithVatInEuroPerYear) + ' €',
        'Erwartete Reduzierung durch Netzentgelte aus § 14a EnWG Modul 1**': formatNumber(slice.creditFromReducedGridFee, '', true) + ' €',
        'Erwartete Reduzierung durch Einspeisung***': formatNumber(slice.totalCostsCappedByGridExportPayment, '', true) + ' €',
        'Erwarteter Gesamtpreis im 1. Lieferjahr': formatNumber(slice.totalCostsNotCappedByGridExportPayment) + ' €',
        smallPrint: '**Voraussetzung für die Reduzierung der Netzentgelte ist eine steuerbare Verbrauchseinrichtung nach § 14a EnWG und gemäß AGB Punkt 11.',
        smallPrint2: '***Darüber hinausgehende Einspeisung wird Ihnen mit der Jahresabrechnung ' +
          'ausgezahlt. Sofern Sie die SENEC.Cloud to go dazu gebucht haben, wird die erwartete überschüssige ' +
          'Einspeisung mit Ihrem SENEC.Cloud to go Abschlag verrechnet.'
      }
    } else {
      data.Section2 = {
        'Erwarteter Gesamtpreis im 1. Lieferjahr': formatNumber(slice.totalPriceWithVatInEuroPerYear) + ' €',
        'Kosten gedeckt durch erwartete Einspeisung**': formatNumber(slice.totalCostsCappedByGridExportPayment) + ' €',
        'Kosten nicht gedeckt durch erwartete Einspeisung**': formatNumber(slice.totalCostsNotCappedByGridExportPayment) + ' €',
        smallPrint: '**Die darüber hinausgehende Einspeisung, die wir in Höhe von ' + totalDifferenceOfEnergyCosts + ' € erwarten, wird Ihnen mit der ' +
          'Jahresabrechnung ausgezahlt. Sofern Sie die SENEC.Cloud to go dazu gebucht haben, wird die erwartete ' +
          'überschüssige Einspeisung mit Ihren Cloud to go Ladekosten verrechnet.'
      }
    }
    data.Section3 = {
      'Monatlicher Abschlag': slice.downPaymentInEuroPerMonth + ' €'
    }
    data.Section4 = {
      'Preisstand zum': today,
      Mindestvertragslaufzeit: slice.contractDurationInMonth + ' Monat ab Belieferung',
      'Kündigungsfrist****': slice.cancellationPeriodInMonth + ' Wochen',
      smallPrint: '****Kündigen Sie nicht rechtzeitig, verlängert sich der Vertrag automatisch auf unbestimmte Zeit. ' +
        'Sie können diesen aber zum Ende der Mindestvertragslaufzeit sowie im Anschluss daran jederzeit ' +
        'mit einer Frist von 4 Wochen kündigen.'
    }
    return data
  }

  function getHeatAndFafData (slice: BaseContract) : ConditionInformation {
    data.Section1 = {
      'Erwarteter Jahresverbrauch': formatNumber(yearlyConsumption) + ' kWh',
      'Grundpreis pro Monat': formatNumber(slice.basePriceWithVatInEuroPerMonth) + ' €',
      'Verbrauchspreis pro kWh': formatNumber(slice.workingPriceWithVatInCtPerKWh) + ' Cent'
    }
    if (heatPumpConsumption === '1') {
      data.Section1['Verbrauchspreis pro kWh'] = data.Section1['Verbrauchspreis pro kWh*']
      delete data.Section1['Verbrauchspreis pro kWh']
      data.Section1['Verbrauchspreis pro kWh*'] = formatNumber(slice.workingPriceWithVatInCtPerKWh) + ' Cent'
      data.Section1.smallPrint = '*Reduzierte Netzentgelte sind nach § 14a EnWG und gemäß AGB Punkt 11 im Verbrauchspreis bereits enthalten.'
    }
    data.Section2 = {
      'Erwarteter Gesamtpreis im 1. Lieferjahr': formatNumber(slice.totalPriceWithVatInEuroPerYear) + ' €'
    }
    data.Section3 = {
      'Monatlicher Abschlag': slice.downPaymentInEuroPerMonth + ' €'
    }
    data.Section4 = {
      'Preisstand zum': today,
      Mindestvertragslaufzeit: slice.contractDurationInMonth + ' Monat ab Belieferung',
      'Kündigungsfrist**': slice.cancellationPeriodInMonth + ' Wochen',
      smallPrint: '**Kündigen Sie nicht rechtzeitig, verlängert sich der Vertrag automatisch auf unbestimmte Zeit. ' +
        'Sie können diesen aber zum Ende der Mindestvertragslaufzeit sowie im Anschluss daran jederzeit ' +
        'mit einer Frist von 4 Wochen kündigen.'
    }
    return data
  }

  function getCloudToGoData (slice: CtgContract) : ConditionInformation {
    data.Section2 = {
      'Monatlicher Abschlag': formatNumber(slice.ctgTotalCostsMinusRenumerartionWithoutVatInEuroPerMonthRounded) + ' €'
    }
    data.Section3 = {
      'Jährliche Fahrleistung': formatNumber(slice.ctgMileagePerYear) + ' km',
      'Stromverbrauch des Autos': formatNumber(slice.ctgConsumptionInKwhPerHundred) + ' kWh / 100km',
      'Anteil zu Hause geladen': formatNumber(slice.ctgPercentageChargedAtHome) + ' %'
    }
    data.Section4 = {
      'Preisstand zum': today,
      Mindestvertragslaufzeit: slice.contractDurationInMonth + ' Monat ab Belieferung',
      'Kündigungsfrist***': slice.cancellationPeriodInMonth + ' Wochen',
      smallPrint: '***Kündigen Sie nicht rechtzeitig, verlängert sich der Vertrag automatisch auf unbestimmte Zeit. ' +
        'Sie können diesen aber zum Ende der Mindestvertragslaufzeit sowie im Anschluss daran jederzeit ' +
        'mit einer Frist von 4 Wochen kündigen.'
    }

    data.Section1 = {
      'Erwartete Lademenge pro Jahr': formatNumber(slice.totalEnergyAmount) + ' kWh',
      'Erwartete Ladekosten pro Jahr*': formatNumber(slice.totalPriceWithVatInEuroPerYear) + ' €',
      'Kosten gedeckt durch erwartete Einspeisung**': formatNumber(slice.totalCostsCappedByGridExportPayment) + ' €',
      'Kosten nicht gedeckt durch erwartete Einspeisung**': formatNumber(slice.totalCostsNotCappedByGridExportPayment) + ' €',
      smallPrint: '*Die erwarteten Ladekosten pro Jahr errechnen sich nach einem Durchschnittspreis von ' +
        '75,96 Cent (brutto) pro kWh. Kosten für die Ladevorgänge werden gemäß den tatsächlich angefallen ' +
        'Kosten über die Nutzung des EnBW Hypernetzes EnBW mobility+ weiterberechnet.',
      smallPrint2: '**Die erwartete überschüssige Einspeisung wird mit Ihren erwarteten Ladekosten ' +
        'verrechnet und senkt so ihren monatlichen Abschlag.'
    }
    return data
  }

  function setConditionDataByProductType (productType: string, slice: any) {
    switch (productType) {
      case 'Cloud to go':
        smallPrint = 'Bruttopreise inkl. 19% Umsatzsteuer und aller sonstiger Preisbestandteile.\n' +
          'Weitere Informationen können Sie in den Dokumenten zum Tarif nachlesen.'
        return getCloudToGoData(slice)
      case 'Cloud':
        return getCloudData(slice)
      case 'Wärme':
      case 'Family & Friends 1':
      case 'Family & Friends 2':
        return getHeatAndFafData(slice)
      default:
        throw new Error('Invalid product type: ' + productType)
    }
  }

  const conditionData = setConditionDataByProductType(productType, slice)

  return (
    <div className="tariff-condition">
      {Object.entries(conditionData).map(([sectionKey, sectionValue]) => (
        <React.Fragment key={sectionKey}>
          <div className="section">
            {Object.entries(sectionValue).map(([key, value]) => {
              if (key === 'smallPrint' || key === 'smallPrint2') {
                return (
                  <div key={key} className="text-sm md:w-3/4 mx-auto">
                    {value as ReactNode}
                  </div>
                )
              } else {
                return (
                  <div key={key} className="flex justify-between md:w-3/4 mx-auto">
                    <div className="text-md">{key}</div>
                    <div className="text-md">{value as ReactNode}</div>
                  </div>
                )
              }
            })}
          </div>
          {
            Object.keys(sectionValue).length > 0 &&
            <hr className="divider justify-between md:w-3/4 mx-auto border-b border-gray-200 my-2"/>
          }
        </React.Fragment>
      ))}
      <div className="section text-xs md:w-3/4 mx-auto">
        {smallPrint}
      </div>
      <div className="files text-lg font-bold md:w-3/4 mx-auto">
        <TariffConditionDownloads
        productType={productType}/>
      </div>
    </div>
  )
}

export default Component
